import { useEffect, useMemo, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import SEO from "../components/site/SEO";
import Reveal from "../components/site/Reveal";
import { useDevis } from "../components/site/DevisForm";
import { useConfig } from "../components/Layout";
import NotFound from "./NotFound";
import api, { assetUrl } from "../lib/api";

const VIDE = { largeur: "", hauteur: "", diametre: "", marque: "", saison: "", etat: "", prixMax: "" };

function dimension(p) {
  return `${p.largeur}/${p.hauteur}/R${p.diametre} ${p.indice_charge}${p.indice_vitesse}`;
}

export default function Catalogue() {
  const [pneus, setPneus] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [filtres, setFiltres] = useState(VIDE);
  const [panneauOuvert, setPanneauOuvert] = useState(false);
  const { openDevis } = useDevis();
  const config = useConfig();

  useEffect(() => {
    api.get("/pneus")
      .then((res) => setPneus(res.data))
      .catch(() => {})
      .finally(() => setChargement(false));
  }, []);

  const options = useMemo(() => {
    const uniq = (k) => [...new Set(pneus.map((p) => p[k]).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), "fr", { numeric: true }));
    return {
      largeur: uniq("largeur"), hauteur: uniq("hauteur"), diametre: uniq("diametre"),
      marque: uniq("marque"),
    };
  }, [pneus]);

  const resultats = useMemo(() => pneus.filter((p) => {
    if (filtres.largeur && p.largeur !== filtres.largeur) return false;
    if (filtres.hauteur && p.hauteur !== filtres.hauteur) return false;
    if (filtres.diametre && p.diametre !== filtres.diametre) return false;
    if (filtres.marque && p.marque !== filtres.marque) return false;
    if (filtres.saison && p.saison !== filtres.saison) return false;
    if (filtres.etat && p.etat !== filtres.etat) return false;
    if (filtres.prixMax && p.prix > Number(filtres.prixMax)) return false;
    return true;
  }), [pneus, filtres]);

  const setF = (k) => (e) => setFiltres((f) => ({ ...f, [k]: e.target.value }));

  const Select = ({ id, label, valeur, onChange, options: opts }) => (
    <div>
      <label htmlFor={id} className="block text-sm font-heading uppercase tracking-wider text-brand-dark mb-2">{label}</label>
      <select id={id} data-testid={`filtre-${id}`} value={valeur} onChange={onChange}
        className="w-full bg-white border-b-2 border-gray-300 focus:border-brand-red px-3 py-3 outline-none font-body text-brand-dark transition-colors">
        <option value="">Tous</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  const panneauFiltres = (
    <div className="space-y-5">
      <Select id="largeur" label="Largeur" valeur={filtres.largeur} onChange={setF("largeur")} options={options.largeur} />
      <Select id="hauteur" label="Hauteur" valeur={filtres.hauteur} onChange={setF("hauteur")} options={options.hauteur} />
      <Select id="diametre" label="Diamètre" valeur={filtres.diametre} onChange={setF("diametre")} options={options.diametre} />
      <Select id="marque" label="Marque" valeur={filtres.marque} onChange={setF("marque")} options={options.marque} />
      <Select id="saison" label="Saison" valeur={filtres.saison} onChange={setF("saison")} options={["Été", "Hiver", "4 saisons"]} />
      <Select id="etat" label="État" valeur={filtres.etat} onChange={setF("etat")} options={["Neuf", "Occasion"]} />
      <div>
        <label htmlFor="prixmax" className="block text-sm font-heading uppercase tracking-wider text-brand-dark mb-2">
          Prix maximum {filtres.prixMax ? `: ${filtres.prixMax} €` : ""}
        </label>
        <input id="prixmax" data-testid="filtre-prixmax" type="range" min="30" max="150" step="5"
          value={filtres.prixMax || 150} onChange={setF("prixMax")} className="w-full accent-[#E8492F]" />
      </div>
      <button data-testid="filtres-reinitialiser" onClick={() => setFiltres(VIDE)}
        className="w-full bg-brand-dark text-white font-heading font-semibold uppercase tracking-wider py-3 hover:bg-black transition-colors">
        Tout effacer
      </button>
    </div>
  );

  if (config && config.catalogue_visible === false) {
    return <NotFound />;
  }

  return (
    <>
      <SEO
        title="Catalogue pneus neufs et occasions au Cannet | Promo Pneus"
        description="Pneus neufs et occasions au Cannet, près de Cannes : Michelin, Continental, Goodyear… Prix indicatifs affichés, commande sous 24 à 48 h."
      />
      <section className="bg-brand-dark py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base mb-3">
              {"//"} Neufs et occasions
            </p>
            <h1 className="font-heading font-bold uppercase text-5xl sm:text-6xl md:text-7xl text-white tracking-wide leading-none">
              Catalogue pneus
            </h1>
            <p className="mt-5 text-lg text-white bg-white/10 border-l-4 border-brand-red px-5 py-4 max-w-3xl leading-relaxed" data-testid="catalogue-mention">
              Prix indicatifs, montage non inclus. Nous consulter pour toute dimension non listée — nous commandons sous 24 à 48 h.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            data-testid="filtres-bascule-mobile"
            onClick={() => setPanneauOuvert(!panneauOuvert)}
            className="lg:hidden w-full mb-6 flex items-center justify-center gap-2 bg-brand-dark text-white font-heading font-semibold uppercase tracking-wider text-lg py-4"
            aria-expanded={panneauOuvert}
          >
            <SlidersHorizontal size={20} aria-hidden="true" />
            {panneauOuvert ? "Masquer les filtres" : "Filtrer les pneus"}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
            <aside className={`${panneauOuvert ? "block" : "hidden"} lg:block bg-brand-light p-6 h-max lg:sticky lg:top-28`} data-testid="panneau-filtres">
              <h2 className="font-heading font-semibold uppercase text-2xl text-brand-dark mb-5">Filtres</h2>
              {panneauFiltres}
            </aside>

            <div className="lg:col-span-3">
              <p className="text-brand-body mb-6 font-body" data-testid="catalogue-compteur" aria-live="polite">
                {chargement ? "Chargement du catalogue…" : `${resultats.length} pneu${resultats.length > 1 ? "s" : ""} disponible${resultats.length > 1 ? "s" : ""}`}
              </p>
              {!chargement && resultats.length === 0 && (
                <div className="bg-brand-light p-10 text-center" data-testid="catalogue-vide">
                  <p className="font-heading font-semibold uppercase text-2xl text-brand-dark mb-2">Aucun pneu ne correspond</p>
                  <p className="text-brand-body">Essayez d'élargir vos critères, ou contactez-nous : nous commandons sous 24 à 48 h.</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {resultats.map((p) => (
                  <article key={p.id} data-testid={`pneu-carte-${p.id}`}
                    className="bg-white border border-gray-200 flex flex-col group hover:border-brand-red transition-colors">
                    <div className="relative overflow-hidden">
                      <img src={assetUrl(p.photo)} alt={`Pneu ${p.marque} ${p.modele} ${dimension(p)}`}
                        className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <span className={`absolute top-3 left-3 font-heading font-semibold uppercase text-sm tracking-wider px-3 py-1 ${p.etat === "Neuf" ? "bg-brand-red text-white" : "bg-brand-dark text-white"}`}>
                        {p.etat}
                      </span>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h2 className="font-heading font-semibold uppercase text-xl text-brand-dark leading-tight">
                        {p.marque} {p.modele}
                      </h2>
                      <p className="font-heading text-brand-navy text-lg font-semibold mt-1">{dimension(p)}</p>
                      <p className="text-brand-body text-sm mt-1">{p.saison}</p>
                      <div className="flex items-end justify-between mt-4 mb-4">
                        <p className="font-heading font-bold text-3xl text-brand-red">{p.prix} €</p>
                        <p className="text-xs text-brand-body">prix indicatif</p>
                      </div>
                      <button
                        data-testid={`pneu-interesse-${p.id}`}
                        onClick={() => openDevis(`Bonjour, ce pneu m'intéresse : ${p.marque} ${p.modele} ${dimension(p)} (${p.saison}, ${p.etat}, ${p.prix} €). `)}
                        className="mt-auto bg-brand-dark hover:bg-brand-red text-white font-heading font-semibold uppercase tracking-wider py-3.5 transition-colors"
                      >
                        Ce pneu m'intéresse
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
